import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Import the RTK Query API slice so we can listen to its matched actions
// in extraReducers (same pattern as RTK Query tutorial Part 8: notifications slice).
import { variantApi } from '../../api/variantApi';
import { extractLockedByFromError } from './reducerUtils';
import { VariantAssessmentStage, VariantWorkflow, WorkflowStep } from './types';

const initialState: VariantWorkflow = {
  workflow_step: undefined,
  stages: [] as VariantAssessmentStage[],
  interpretation_zone: undefined,
  variant_transcripts: [],
  references: [],
  lock: null,
  institutes: null,
  frequencyDataNeedsRecalculation: false,
  isRecalculatingArguments: false,
};

export const VariantWorkFlowSlice = createSlice({
  name: 'variant-workflow',
  initialState,
  reducers: {
    resetVariantDependentData: (state) => {
      // Reset all variant-dependent slice state to initial values
      // This is called when navigating to a different variant to prevent stale data
      state.workflow_step = undefined;
      state.stages = [];
      state.lock = null;
      state.institutes = null;
      state.frequencyDataNeedsRecalculation = false;
      state.isRecalculatingArguments = false;
      state.interpretation_zone = undefined;
      state.references = [];
      state.variant_transcripts = [];
      state.linkouts = undefined;
    },

    setWorkflowStep: (
      state,
      action: PayloadAction<WorkflowStep | undefined>,
    ) => {
      state.workflow_step = action.payload;
    },

    setInstitutes: (state, action: PayloadAction<number[] | null>) => {
      state.institutes = action.payload;
    },

    clearVariantLock: (state) => {
      state.lock = null;
    },

    clearFrequencyDataNeedsRecalculation: (state) => {
      state.frequencyDataNeedsRecalculation = false;
    },

    setFrequencyDataNeedsRecalculation: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.frequencyDataNeedsRecalculation = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      // -- Variant Lock --------------------------------------------------------
      .addMatcher(
        variantApi.endpoints.acquireVariantLock.matchFulfilled,
        (state, action) => {
          state.lock = action.payload;
        },
      )
      // When acquiring a lock fails with 423 (Locked), hydrate the lock in state
      // so the UI can switch to read-only and show the lock indicator.
      // transformErrorResponse in variantApi gives us { locked_by, message }
      // which extractLockedByFromError already knows how to handle.
      .addMatcher(
        variantApi.endpoints.acquireVariantLock.matchRejected,
        (state, action) => {
          const lock = extractLockedByFromError(action);
          if (lock) {
            state.lock = lock;
          }
        },
      )
      .addMatcher(
        variantApi.endpoints.refreshVariantLock.matchFulfilled,
        (state, action) => {
          state.lock = action.payload;
        },
      )
      .addMatcher(
        variantApi.endpoints.refreshAndRecalculateVariantData.matchFulfilled,
        (state, action) => {
          if (action.payload.lock) {
            state.lock = action.payload.lock;
          }
        },
      )
      .addMatcher(
        variantApi.endpoints.releaseVariantLock.matchFulfilled,
        (state) => {
          state.lock = null;
        },
      )
      // If the DELETE call itself fails (network error, server gone), still clear
      // the lock from the client's perspective. The server-side lock will expire
      // naturally. This matches the original behaviour: release was always
      // treated as final from the client's side.
      .addMatcher(
        variantApi.endpoints.releaseVariantLock.matchRejected,
        (state) => {
          state.lock = null;
        },
      )
      // -- setAsDefaultMaxCAFScoring → trigger recalculation flag --------------
      .addMatcher(
        variantApi.endpoints.setAsDefaultMaxCAFScoring.matchFulfilled,
        (state) => {
          state.frequencyDataNeedsRecalculation = true;
        },
      )
      // -- updateGeneTopVariantsThreshold → trigger recalculation flag ----------
      .addMatcher(
        variantApi.endpoints.updateGeneTopVariantsThreshold.matchFulfilled,
        (state) => {
          state.frequencyDataNeedsRecalculation = true;
        },
      )
      // -- Recalculation lifecycle ---------------------------------------------
      .addMatcher(
        variantApi.endpoints.recalculateAutoAssessmentArguments.matchPending,
        (state) => {
          state.isRecalculatingArguments = true;
        },
      )
      .addMatcher(
        variantApi.endpoints.recalculateAutoAssessmentArguments.matchFulfilled,
        (state) => {
          state.isRecalculatingArguments = false;
          // NOTE: frequencyDataNeedsRecalculation is NOT reset here.
          // It is reset explicitly after all data refetches complete
          // (via clearFrequencyDataNeedsRecalculation action from the component).
        },
      )
      .addMatcher(
        variantApi.endpoints.recalculateAutoAssessmentArguments.matchRejected,
        (state) => {
          state.isRecalculatingArguments = false;
        },
      );
  },
});

export const {
  resetVariantDependentData,
  setWorkflowStep,
  setInstitutes,
  clearVariantLock,
  clearFrequencyDataNeedsRecalculation,
  setFrequencyDataNeedsRecalculation,
} = VariantWorkFlowSlice.actions;

export default VariantWorkFlowSlice.reducer;
